import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, Empty } from '@arco-design/web-react'
import { AreaChart, DonutChart, BarChart, type CustomTooltipProps } from '@tremor/react'
import type { DailyData } from '@/api/dashboard'
import type { DashboardChartTab } from '@/hooks/useDashboardData'
import { renderQuota, formatNumberCompact } from '@/utils/quota'
import {
  mergeQuotaRows,
  aggregateByField,
  buildTopKAndOther,
  rankByField,
  stackedSeriesByDate,
  hasChartData,
} from '@/utils/dashboardChartData'

interface ChartsPanelProps {
  data: DailyData[]
  rangeLabel?: string
  activeTab: DashboardChartTab
  onTabChange: (tab: DashboardChartTab) => void
  isAdmin?: boolean
}

const SERIES_COLORS: (
  | 'indigo'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'cyan'
  | 'slate'
)[] = ['indigo', 'emerald', 'amber', 'violet', 'cyan', 'slate']

const LEGEND_HEX: Record<(typeof SERIES_COLORS)[number], string> = {
  indigo: 'var(--brand-primary)',
  emerald: 'var(--color-success)',
  amber: 'var(--color-warning)',
  violet: 'var(--brand-secondary)',
  cyan: 'var(--color-info)',
  slate: 'var(--color-neutral-text)',
}

function QuotaStackedTooltip({ active, payload, label }: CustomTooltipProps) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const rows = payload
    .map((p) => ({
      name: String(p.name ?? p.dataKey),
      v: Number(p.value) || 0,
    }))
    .filter((r) => r.v > 0)
    .sort((a, b) => b.v - a.v)
  const total = rows.reduce((s, r) => s + r.v, 0)
  return (
    <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 shadow-md text-[13px] min-w-[200px]">
      <p className="text-xs text-[var(--console-text-muted)] mb-2 font-medium tabular-nums">{String(label)}</p>
      <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
        {rows.map((r) => {
          const pct = total > 0 ? ((r.v / total) * 100).toFixed(1) : '0.0'
          return (
            <li
              key={r.name}
              className="flex justify-between gap-4 text-xs text-[var(--console-text-body)] leading-tight"
            >
              <span className="truncate" title={r.name}>
                {r.name}
              </span>
              <span className="tabular-nums text-[var(--console-text)] shrink-0">
                {renderQuota(r.v)} <span className="text-[var(--console-text-subtle)]">({pct}%)</span>
              </span>
            </li>
          )
        })}
      </ul>
      <div className="mt-2 border-t border-[var(--console-divider)] pt-2 text-xs font-semibold text-[var(--console-text-h)] flex justify-between">
        <span>{t('tt.charts.total')}</span>
        <span className="tabular-nums">{renderQuota(total)}</span>
      </div>
    </div>
  )
}

function CountStackedTooltip({ active, payload, label }: CustomTooltipProps) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const rows = payload
    .map((p) => ({
      name: String(p.name ?? p.dataKey),
      v: Number(p.value) || 0,
    }))
    .filter((r) => r.v > 0)
    .sort((a, b) => b.v - a.v)
  const total = rows.reduce((s, r) => s + r.v, 0)
  return (
    <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 shadow-md text-[13px] min-w-[200px]">
      <p className="text-xs text-[var(--console-text-muted)] mb-2 font-medium tabular-nums">{String(label)}</p>
      <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
        {rows.map((r) => {
          const pct = total > 0 ? ((r.v / total) * 100).toFixed(1) : '0.0'
          return (
            <li
              key={r.name}
              className="flex justify-between gap-4 text-xs text-[var(--console-text-body)] leading-tight"
            >
              <span className="truncate" title={r.name}>
                {r.name}
              </span>
              <span className="tabular-nums text-[var(--console-text)] shrink-0">
                {t('tt.charts.callsCount', { count: formatNumberCompact(r.v) })}{' '}
                <span className="text-[var(--console-text-subtle)]">({pct}%)</span>
              </span>
            </li>
          )
        })}
      </ul>
      <div className="mt-2 border-t border-[var(--console-divider)] pt-2 text-xs font-semibold text-[var(--console-text-h)] flex justify-between">
        <span>{t('tt.charts.totalCallsLabel')}</span>
        <span className="tabular-nums">{formatNumberCompact(total)}</span>
      </div>
    </div>
  )
}

function CountBarTooltip({ active, payload, label }: CustomTooltipProps) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const p = payload[0]
  const n = Number(p.value) || 0
  return (
    <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 shadow-md text-xs">
      <p className="text-[var(--console-text-muted)] mb-1 max-w-xs break-all" title={String(label)}>
        {String(label)}
      </p>
      <p className="text-[var(--console-text)] font-medium tabular-nums">
        {t('tt.charts.callsCount', { count: formatNumberCompact(n) })}
      </p>
    </div>
  )
}

function QuotaBarTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null
  const p = payload[0]
  const n = Number(p.value) || 0
  return (
    <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2 shadow-md text-xs">
      <p className="text-[var(--console-text-muted)] mb-1 max-w-xs break-all" title={String(label)}>
        {String(label)}
      </p>
      <p className="text-[var(--console-text)] font-medium tabular-nums">{renderQuota(n)}</p>
    </div>
  )
}

function useDonutSliceTooltip(total: number, kind: 'quota' | 'count') {
  const { t } = useTranslation()
  return useMemo(() => {
    return function DonutSliceTooltip({ active, payload }: CustomTooltipProps) {
      if (!active || !payload?.length) return null
      const p = payload[0] as {
        value?: string | number
        name?: string
        dataKey?: string
        color?: string
        fill?: string
        payload?: { model_name?: string }
      }
      const n = Number(p.value) || 0
      const name = String(p.name ?? p.payload?.model_name ?? p.dataKey ?? '')
      const pct = total > 0 ? (n / total) * 100 : 0
      const swatch = p.color ?? p.fill ?? 'var(--color-neutral-text)'
      return (
        <div className="rounded-lg border border-[var(--console-border)] bg-[var(--console-surface)] px-3 py-2.5 shadow-md text-xs min-w-[150px] max-w-[min(100vw-2rem,280px)]">
          <div className="flex items-start gap-2 mb-1.5">
            <span
              className="w-2 h-2 rounded-full mt-0.5 shrink-0"
              style={{ backgroundColor: swatch }}
            />
            <p className="text-[var(--console-text-muted)] font-medium break-words leading-snug flex-1">{name}</p>
          </div>
          <div className="pl-4 space-y-1">
            <p className="text-[var(--console-text)] tabular-nums font-bold text-sm">
              {kind === 'quota'
                ? renderQuota(n)
                : t('tt.charts.callsCount', { count: formatNumberCompact(n) })}
            </p>
            <p className="text-[11px] text-[var(--console-text-muted)] tabular-nums">
              {t('tt.charts.ofTotal', { pct: pct.toFixed(1) })}
            </p>
          </div>
        </div>
      )
    }
  }, [total, kind, t])
}

type DonutAggRow = { name: string; quota: number; count: number }

function DonutQuotaPanel({
  rows,
  total,
  chartData,
  colors,
}: {
  rows: DonutAggRow[]
  total: number
  chartData: { model_name: string; quota: number }[]
  colors: (typeof SERIES_COLORS)[number][]
}) {
  const { t } = useTranslation()
  const quotaTooltip = useDonutSliceTooltip(total, 'quota')

  return (
    <div className="mt-4 flex flex-col lg:flex-row items-stretch justify-center gap-8 lg:gap-16 min-h-[320px] max-w-5xl mx-auto">
      <div className="shrink-0 w-[280px] sm:w-[340px] relative h-80 mx-auto lg:mx-0">
        <DonutChart
          className="h-80 w-full"
          data={chartData}
          category="quota"
          index="model_name"
          colors={colors}
          valueFormatter={renderQuota}
          customTooltip={quotaTooltip}
          showLabel={false}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center flex flex-col items-center justify-center mt-2">
            <p className="text-sm font-medium text-[var(--console-text-muted)] mb-1">{t('tt.charts.totalQuotaLabel')}</p>
            <p className="text-[28px] font-bold tabular-nums tracking-tight text-[var(--console-text-h)]">
              {renderQuota(total)}
            </p>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[420px] shrink-0 border-t lg:border-t-0 lg:border-l border-[var(--console-divider)] pt-6 lg:pt-0 lg:pl-12 flex flex-col justify-center">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] font-semibold text-[var(--console-text-h)]">{t('tt.charts.modelQuotaDetail')}</p>
          <p className="text-[12px] text-[var(--console-text-muted)]">{t('tt.charts.share')}</p>
        </div>
        <ul className="space-y-1 max-h-72 overflow-y-auto pr-2">
          {rows.map((d, i) => {
            const pct = total > 0 ? (d.quota / total) * 100 : 0
            const c = colors[i] ?? 'slate'
            return (
              <li
                key={d.name}
                className="flex items-center justify-between gap-4 text-[13px] px-2 py-2 rounded-lg hover:bg-[var(--console-hover-row)] transition-colors group"
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: LEGEND_HEX[c] }}
                  />
                  <span
                    className="truncate text-[var(--console-text-body)] group-hover:text-[var(--console-text-h)] transition-colors font-medium"
                    title={d.name}
                  >
                    {d.name}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-right">
                  <span className="tabular-nums text-[var(--console-text-h)] font-semibold">{renderQuota(d.quota)}</span>
                  <span className="tabular-nums text-[var(--console-text-muted)] w-12 text-right text-xs bg-[var(--color-neutral-bg)] px-1.5 py-0.5 rounded">
                    {pct.toFixed(1)}%
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function DonutCountPanel({
  rows,
  total,
  chartData,
  colors,
}: {
  rows: DonutAggRow[]
  total: number
  chartData: { model_name: string; count: number }[]
  colors: (typeof SERIES_COLORS)[number][]
}) {
  const { t } = useTranslation()
  const countTooltip = useDonutSliceTooltip(total, 'count')

  return (
    <div className="mt-4 flex flex-col lg:flex-row items-stretch justify-center gap-8 lg:gap-16 min-h-[320px] max-w-5xl mx-auto">
      <div className="shrink-0 w-[280px] sm:w-[340px] relative h-80 mx-auto lg:mx-0">
        <DonutChart
          className="h-80 w-full"
          data={chartData}
          category="count"
          index="model_name"
          colors={colors}
          valueFormatter={formatNumberCompact}
          customTooltip={countTooltip}
          showLabel={false}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center flex flex-col items-center justify-center mt-2">
            <p className="text-sm font-medium text-[var(--console-text-muted)] mb-1">{t('tt.charts.totalCallsLabel')}</p>
            <p className="text-[28px] font-bold tabular-nums tracking-tight text-[var(--console-text-h)]">
              {formatNumberCompact(total)}{' '}
              <span className="text-base font-semibold text-[var(--console-text-muted)] ml-0.5">
                {t('tt.charts.timesUnit')}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-[420px] shrink-0 border-t lg:border-t-0 lg:border-l border-[var(--console-divider)] pt-6 lg:pt-0 lg:pl-12 flex flex-col justify-center">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] font-semibold text-[var(--console-text-h)]">{t('tt.charts.modelCallsDetail')}</p>
          <p className="text-[12px] text-[var(--console-text-muted)]">{t('tt.charts.share')}</p>
        </div>
        <ul className="space-y-1 max-h-72 overflow-y-auto pr-2">
          {rows.map((d, i) => {
            const pct = total > 0 ? (d.count / total) * 100 : 0
            const c = colors[i] ?? 'slate'
            return (
              <li
                key={d.name}
                className="flex items-center justify-between gap-4 text-[13px] px-2 py-2 rounded-lg hover:bg-[var(--console-hover-row)] transition-colors group"
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ background: LEGEND_HEX[c] }}
                  />
                  <span
                    className="truncate text-[var(--console-text-body)] group-hover:text-[var(--console-text-h)] transition-colors font-medium"
                    title={d.name}
                  >
                    {d.name}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-right">
                  <span className="tabular-nums text-[var(--console-text-h)] font-semibold">
                    {t('tt.charts.callsCount', { count: formatNumberCompact(d.count) })}
                  </span>
                  <span className="tabular-nums text-[var(--console-text-muted)] w-12 text-right text-xs bg-[var(--color-neutral-bg)] px-1.5 py-0.5 rounded">
                    {pct.toFixed(1)}%
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

function TabEmptyState() {
  const { t } = useTranslation()
  return (
    <div className="h-72 md:h-80 flex items-center justify-center mt-4">
      <Empty description={t('tt.charts.noDataInRange')} />
    </div>
  )
}

function ChartsEmptyState() {
  const { t } = useTranslation()
  return (
    <div className="tt-card p-4 md:p-6">
      <Empty
        description={
          <div className="text-center">
            <p className="text-sm font-medium text-[var(--console-text-h)]">{t('tt.dashboard.noUsageData')}</p>
            <p className="text-xs text-[var(--console-text-muted)] mt-1">{t('tt.dashboard.noUsageDataDesc')}</p>
          </div>
        }
      />
    </div>
  )
}

const USER_TABS: DashboardChartTab[] = ['user-quota-rank', 'user-quota-trend']

/**
 * 仪表盘图表区：7 类可切换图表，数据来自同一份 quotaData
 */
export function ChartsPanel({
  data,
  rangeLabel,
  activeTab,
  onTabChange,
  isAdmin = false,
}: ChartsPanelProps) {
  const { t } = useTranslation()
  const hasData = hasChartData(data)
  const otherLabel = t('tt.charts.other')
  const unknownLabel = t('未知')

  const merged = useMemo(() => mergeQuotaRows(data, unknownLabel), [data, unknownLabel])
  const byModel = useMemo(() => aggregateByField(merged, 'model'), [merged])
  const byUser = useMemo(() => aggregateByField(merged, 'username'), [merged])

  const quotaDonutData = useMemo(
    () => buildTopKAndOther(byModel, 'quota', 5, otherLabel),
    [byModel, otherLabel]
  )
  const countDonutData = useMemo(
    () => buildTopKAndOther(byModel, 'count', 5, otherLabel),
    [byModel, otherLabel]
  )

  const totalQuota = useMemo(() => byModel.reduce((s, m) => s + m.quota, 0), [byModel])
  const totalCalls = useMemo(() => byModel.reduce((s, m) => s + m.count, 0), [byModel])

  const quotaDonutColors = useMemo(
    () => quotaDonutData.map((_, i) => SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)]),
    [quotaDonutData]
  )
  const countDonutColors = useMemo(
    () => countDonutData.map((_, i) => SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)]),
    [countDonutData]
  )

  const callsTrend = useMemo(
    () => stackedSeriesByDate(merged, 'count', 'model', 5, otherLabel),
    [merged, otherLabel]
  )
  const userQuotaTrend = useMemo(
    () => stackedSeriesByDate(merged, 'quota', 'username', 5, otherLabel),
    [merged, otherLabel]
  )

  const modelQuotaRank = useMemo(() => rankByField(byModel, 'quota', 15), [byModel])
  const modelCallsRank = useMemo(() => rankByField(byModel, 'count', 15), [byModel])
  const userQuotaRank = useMemo(() => rankByField(byUser, 'quota', 15), [byUser])

  if (!hasData) {
    return <ChartsEmptyState />
  }

  const effectiveTab =
    !isAdmin && USER_TABS.includes(activeTab) ? 'quota-dist' : activeTab

  const tabDefs: { key: DashboardChartTab; title: string; adminOnly?: boolean }[] = [
    { key: 'quota-dist', title: t('tt.charts.distribution') },
    { key: 'calls-trend', title: t('tt.charts.callsTrend') },
    { key: 'calls-dist', title: t('tt.charts.callsDistribution') },
    { key: 'model-quota-rank', title: t('tt.charts.modelQuotaRank') },
    { key: 'model-calls-rank', title: t('tt.charts.callsRank') },
    { key: 'user-quota-rank', title: t('tt.charts.userQuotaRank'), adminOnly: true },
    { key: 'user-quota-trend', title: t('tt.charts.userQuotaTrend'), adminOnly: true },
  ]

  return (
    <div className="tt-card p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-sm font-semibold text-[var(--console-text-h)]">{t('tt.charts.usageOverview')}</h3>
        {rangeLabel && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-medium text-[var(--console-text-muted)] bg-[var(--console-surface-subtle)] border border-[var(--console-border)] truncate max-w-[min(100%,420px)]">
            {rangeLabel}
          </span>
        )}
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        <Tabs
          activeTab={effectiveTab}
          onChange={(key) => onTabChange(key as DashboardChartTab)}
        >
          {tabDefs
            .filter((tab) => !tab.adminOnly || isAdmin)
            .map((tab) => (
              <Tabs.TabPane key={tab.key} title={tab.title}>
                {tab.key === 'quota-dist' &&
                  (quotaDonutData.length > 0 ? (
                    <DonutQuotaPanel
                      rows={quotaDonutData}
                      total={totalQuota}
                      chartData={quotaDonutData.map((d) => ({ model_name: d.name, quota: d.quota }))}
                      colors={quotaDonutColors}
                    />
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'calls-trend' &&
                  (callsTrend.rows.length > 0 && callsTrend.categories.length > 0 ? (
                    <div className="mt-2">
                      <AreaChart
                        className="h-72 md:h-80 mt-2"
                        data={callsTrend.rows}
                        index="date"
                        categories={callsTrend.categories}
                        colors={callsTrend.categories.map((_, i) => SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)])}
                        valueFormatter={formatNumberCompact}
                        showLegend
                        yAxisWidth={56}
                        stack
                        customTooltip={CountStackedTooltip}
                        enableLegendSlider
                      />
                    </div>
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'calls-dist' &&
                  (countDonutData.length > 0 ? (
                    <DonutCountPanel
                      rows={countDonutData}
                      total={totalCalls}
                      chartData={countDonutData.map((d) => ({ model_name: d.name, count: d.count }))}
                      colors={countDonutColors}
                    />
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'model-quota-rank' &&
                  (modelQuotaRank.length > 0 ? (
                    <div className="mt-4">
                      <BarChart
                        className="h-72 md:h-80"
                        layout="vertical"
                        data={modelQuotaRank.map((m) => ({
                          model_name: m.name,
                          quota: m.quota,
                        }))}
                        index="model_name"
                        categories={['quota']}
                        colors={['indigo']}
                        valueFormatter={renderQuota}
                        showLegend={false}
                        yAxisWidth={160}
                        customTooltip={QuotaBarTooltip}
                      />
                    </div>
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'model-calls-rank' &&
                  (modelCallsRank.length > 0 ? (
                    <div className="mt-4">
                      <BarChart
                        className="h-72 md:h-80"
                        layout="vertical"
                        data={modelCallsRank.map((m) => ({
                          model_name: m.name,
                          count: m.count,
                        }))}
                        index="model_name"
                        categories={['count']}
                        colors={['indigo']}
                        valueFormatter={formatNumberCompact}
                        showLegend={false}
                        yAxisWidth={160}
                        customTooltip={CountBarTooltip}
                      />
                    </div>
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'user-quota-rank' &&
                  isAdmin &&
                  (userQuotaRank.length > 0 ? (
                    <div className="mt-4">
                      <BarChart
                        className="h-72 md:h-80"
                        layout="vertical"
                        data={userQuotaRank.map((u) => ({
                          username: u.name,
                          quota: u.quota,
                        }))}
                        index="username"
                        categories={['quota']}
                        colors={['violet']}
                        valueFormatter={renderQuota}
                        showLegend={false}
                        yAxisWidth={160}
                        customTooltip={QuotaBarTooltip}
                      />
                    </div>
                  ) : (
                    <TabEmptyState />
                  ))}

                {tab.key === 'user-quota-trend' &&
                  isAdmin &&
                  (userQuotaTrend.rows.length > 0 && userQuotaTrend.categories.length > 0 ? (
                    <div className="mt-2">
                      <AreaChart
                        className="h-72 md:h-80 mt-2"
                        data={userQuotaTrend.rows}
                        index="date"
                        categories={userQuotaTrend.categories}
                        colors={userQuotaTrend.categories.map((_, i) =>
                          SERIES_COLORS[Math.min(i, SERIES_COLORS.length - 1)]
                        )}
                        valueFormatter={renderQuota}
                        showLegend
                        yAxisWidth={80}
                        stack
                        customTooltip={QuotaStackedTooltip}
                        enableLegendSlider
                      />
                    </div>
                  ) : (
                    <TabEmptyState />
                  ))}
              </Tabs.TabPane>
            ))}
        </Tabs>
      </div>
    </div>
  )
}
